import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams, useLocation } from 'react-router-dom';
import routeParams from 'theme/routeParams';
import StatisticsCards from './statistics/StatisticsCards'
import { Column } from 'styled/alignment/Column'
import WithdrawList from './withdraw/WithdrawList'
import DeclinedTransactions from './declined-transactions/DeclinedTransactions'
import Divider from 'components/_UI/Divider'
import { useUser } from 'context/user/User'
import NotResultDashBoard from 'components/commons/NotResultDashBoard'
import paths from 'routes/paths'
import Loader from 'components/_UI/Loader'
import styled from 'styled-components'
import Text from 'components/_UI/Text'
import financialApi from 'api/requests/financial'
import Modal from 'components/_UI/Modal';
import BankForm from './bank-form/BankForm';
import InvoicesTable from './invoices/InvoicesTable';
import ToastContainer from 'components/_UI/ToastsContainer';
import useToast from 'hooks/useToast';
import establishmentsApi from 'api/requests/establishments';
import { useMyEstablishment } from 'context/establishments/MyEstablishment';
import { useWithdraw } from 'context/financial/Withdraw';
import ReceiptsCalendar from './calendar/ReceiptsCalendar';
import colors from 'theme/colors';
import { Row } from 'styled/alignment/Row';
import { FullyCentered } from 'styled/alignment/Center';
import { Layout, Activity, ArrowDownCircle, Calendar, BarChart2 } from 'lucide-react';
import fonts from 'theme/fonts';
import FinancialStatusIcon from './status/FinancialStatusIcon';
import GeneralTransactions from './general/GeneralTransactions';
import GeneralView from './general/GeneralView';
import FinancialAnalysis from './analysis/FinancialAnalysis';
import breakpoints from 'theme/breakpoints';
import TransactionsList from './transactions/TransactionsList';
import FinancialFilters, { DEFAULT_FILTERS } from './filters/FinancialFilters';
import { panelAccent } from 'theme/panelAccent';

/**
 * `custom` só existe para a barra de filtros lembrar qual chip está aceso.
 * O backend não conhece esse campo, e um `undefined` vira querystring vazia
 * no axios — por isso a limpeza é explícita.
 */
function toApiFilters(filters) {
    const { custom, ...rest } = filters || {};

    return Object.fromEntries(
        Object.entries(rest).filter(([, value]) => value !== undefined && value !== '')
    );
}

export default function Financial() {
    const { myEstablishment, setMyEstablishment } = useMyEstablishment();
    const { withdraw, setWithdraw } = useWithdraw();
    const { user } = useUser()
    const { toasts, toast } = useToast()
    const [searchParams, setSearchParams] = useSearchParams();
    const { search } = useLocation();

    const [isLoading, setIsLoading] = useState(true)
    const [data, setData] = useState(null)
    const [recentTransactions, setRecentTransactions] = useState(null)
    const [paginate, setPaginate] = useState(null)
    const [historyWithdraw, setHistoryWithdraw] = useState(null)
    const [notWithdraw, setNotWithdraw] = useState(false)
    const [showBankForm, setShowBankForm] = useState(false)
    const [showInvoices, setShowInvoices] = useState(false)
    /* O BankForm registra aqui a checagem de alterações não salvas — só ele
       sabe se há algo digitado para perder. */
    const bankFormCloseGuard = useRef(null)
    // const [showCalendar, setShowCalendar] = useState(false)
    const [selectedTab, setSelectedTab] = useState('all');
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [isRefreshingSummary, setIsRefreshingSummary] = useState(false);

    const tabKeys = {
        all: 'all',
        transactions: 'transactions',
        withdraw: 'withdraw',
        calendar: 'calendar',
        analysis: 'analysis',
    }

    const tabs = [
        { name: tabKeys.all, label: 'Visão geral', icon: Layout },
        { name: tabKeys.transactions, label: 'Transações', icon: Activity },
        { name: tabKeys.withdraw, label: 'Saques', icon: ArrowDownCircle },
        { name: tabKeys.calendar, label: 'Calendário', icon: Calendar },
        { name: tabKeys.analysis, label: 'Análises', icon: BarChart2 },
    ];



    const closeCollapse = () => {
        setSearchParams((prevSearchParams) => {
            const newSearchParams = new URLSearchParams(prevSearchParams);

            Object.values(routeParams.financial).map(el => {
                newSearchParams.delete(el)
            })

            return newSearchParams;
        });
    }

    function loadData() {
        Promise.all([
            getFinancial(),
            getEstablishments(),
            getWithdrawData(),
        ]).then(() => {
            setIsLoading(false)
        })
    }

    async function getFinancial() {
        try {
            const { data } = await financialApi.get(toApiFilters(filters))
            setData(data)
            setWithdraw({
                ...withdraw,
                availableValue: data?.balance_available
            })
        } catch (error) {

        }
    }

    /* A visão geral lista transações de pedido, não movimentações de saldo:
       é a venda que o produtor reconhece. Saque tem card próprio ao lado. */
    async function getRecentTransactions() {
        try {
            const { data } = await financialApi.getOrderTransactions({ limit: 5 });
            setRecentTransactions(data?.data);
        } catch (error) {

        }
    }

    async function getWithdrawData(number = 1) {
        try {
            const { data } = await financialApi.getWithdrawData({ page: number })
            setPaginate(data)
            setHistoryWithdraw({
                ...historyWithdraw,
                data: data
            })


            if (data?.data?.length == 0) {
                setNotWithdraw(true)

            }
        } catch (error) {

        }
    }

    async function getEstablishments() {
        if (myEstablishment?.length) {
            return
        }

        try {
            const { data } = await establishmentsApi.my()
            setMyEstablishment(data?.data)
            validateBankFormCollapse(data?.data)
        } catch (error) {

        }
    }

    function validateBankFormCollapse(establishments) {
        if (showBankForm && !establishments?.length) {
            setShowBankForm(false)
            closeCollapse()
        }
    }

    useEffect(() => {
        const bankParam = searchParams.get(routeParams.financial.bank)
        const invoicesParam = searchParams.get(routeParams.financial.invoices)
        // const calendarParams = searchParams.get(routeParams.financial.calendar)

        setShowBankForm(!!bankParam);
        setShowInvoices(!!invoicesParam);
        // setShowCalendar(!!calendarParams)
    }, [search]);

    useEffect(() => {
        setIsLoading(true)
        getRecentTransactions();
        loadData();

    }, [withdraw?.success]);

    /* Trocar de período recarrega só os cards. O loader de página inteira
       piscaria a tela toda a cada clique num chip. */
    const filtersKey = JSON.stringify(toApiFilters(filters));

    useEffect(() => {
        if (isLoading) {
            return
        }

        setIsRefreshingSummary(true)
        getFinancial().finally(() => setIsRefreshingSummary(false))

    }, [filtersKey]);

    function onChangeTab(tabName) {
        setSelectedTab(tabName)
    }

    return (
        <>
            <ToastContainer toasts={toasts} />


            <Column>
                {
                    !isLoading ? (

                        !user?.establishments?.length && user?.role?.id == import.meta.env.VITE_ESTABLISHMENT_ROLE_ID ? (
                            <NotResultDashBoard
                                ilustration={'profile'}
                                text={'Notei que você ainda não possuí seu estabelecimento. Que tal criar seu estabelecimento?'}
                                textButton={'Criar meu estabelecimento'}
                                path={`${paths.dashboard.establishment.profile}?create=1`}
                            />
                        ) : (
                            <>
                                <FinancialFilters filters={filters} onChange={setFilters} />

                                <Refreshable refreshing={isRefreshingSummary}>
                                    <StatisticsCards data={data} />
                                </Refreshable>

                                <Tabs role="tablist">
                                    {tabs.map(el => {
                                        const IconComponent = el.icon;
                                        return (
                                            <TabItem
                                                key={el.name}
                                                role="tab"
                                                aria-selected={el.name === selectedTab}
                                                selected={el.name === selectedTab}
                                                onClick={() => onChangeTab(el.name)}
                                            >
                                                <IconComponent size={15} />
                                                <span>{el.label}</span>
                                                {el.badge && <TabBadge>{el.badge}</TabBadge>}
                                            </TabItem>
                                        );
                                    })}
                                </Tabs>

                                {
                                    selectedTab === tabKeys.all && (
                                        <GeneralView
                                            recentTransactions={recentTransactions}
                                            withdraw={historyWithdraw?.data?.data?.slice(0, 5)}
                                            onSeeAllTransactionsClick={() => onChangeTab(tabKeys.transactions)}
                                            onSeeAllWithdrawClick={() => onChangeTab(tabKeys.withdraw)}
                                        />
                                    )
                                }

                                {
                                    selectedTab === tabKeys.withdraw && (
                                        historyWithdraw?.data?.data?.length > 0 ? (
                                            <WithdrawList
                                                data={historyWithdraw}
                                                currentPage={paginate?.current_page}
                                                totalItems={paginate?.total}
                                                lastPage={paginate?.last_page}
                                                firstPage={paginate?.first_page}
                                                perPage={paginate?.per_page}
                                                onPaginate={(number) => getWithdrawData(number)}
                                            />
                                        ) : (
                                            notWithdraw &&
                                            <NotResultDashBoard
                                                text={
                                                    <>
                                                        <Text name='subtitle'>Históricos de saques</Text>
                                                        <Text>Parece que você ainda não efetuou nenhum saque.</Text>
                                                        <Text marginTop={'5px'}>Verifique se possui algum valor a receber disponivel e faça o primeiro repasse para sua conta bancaria.</Text>
                                                    </>
                                                }
                                                ilustration={'withdraw'}
                                                marginTop={'15px'}
                                            />
                                        )
                                    )
                                }

                                {
                                    selectedTab === tabKeys.transactions && (
                                        <TransactionsList filters={toApiFilters(filters)} />
                                    )
                                }

                                {
                                    selectedTab === tabKeys.calendar && (
                                        <ReceiptsCalendar />
                                    )
                                }

                                {
                                    selectedTab === tabKeys.analysis && (
                                        <FinancialAnalysis filters={toApiFilters(filters)} />
                                    )
                                }

                            </>
                        )
                    ) : (
                        <Loader />
                    )
                }
            </Column>

            {/* <Divider marginTop={'40px'} marginBottom={'0'} opacity={.4} /> */}
            {/* <DeclinedTransactions /> */}

            <Modal
                isOpen={showBankForm}
                onClose={closeCollapse}
                beforeClose={() => bankFormCloseGuard.current ? bankFormCloseGuard.current() : true}
                closeButton
                size='large'
                childrenOverflowY='scroll'
            >
                <BankForm
                    visible={showBankForm}
                    onClose={closeCollapse}
                    closeGuardRef={bankFormCloseGuard}
                    toast={toast}
                />
            </Modal>

            {/* Notas fiscais e dados bancários são as duas ações do cabeçalho:
                abriam em componentes diferentes (gaveta lateral × modal) sem
                motivo. Agora seguem o mesmo padrão. */}
            <Modal
                isOpen={showInvoices}
                onClose={closeCollapse}
                closeButton
                size='large'
                childrenOverflowY='scroll'
            >
                <InvoicesTable closeCollapse={closeCollapse} toast={toast} />
            </Modal>
        </>
    )
}


const Refreshable = styled.div`
    opacity: ${({ refreshing }) => refreshing ? 0.5 : 1};
    pointer-events: ${({ refreshing }) => refreshing ? 'none' : 'auto'};
    transition: opacity 0.15s ease;
`;

const Tabs = styled(Row)`
    margin-top: 20px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 4px;
    overflow: auto;
    gap: 2px;

    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
`;

const TabItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 16px;
    border: none;
    background: ${({ selected }) => selected ? '#F3E8FF' : 'transparent'};
    color: ${({ selected }) => selected ? panelAccent(colors.secondaryPurple) : colors.night};
    font-weight: ${({ selected }) => selected ? '600' : '400'};
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 14px;
    font-family: inherit;
    width: 100%;
    transition: all 0.2s ease;

    &:hover {
        background: ${({ selected }) => selected ? '#F3E8FF' : colors.neutral};
        color: ${({ selected }) => selected ? panelAccent(colors.secondaryPurple) : colors.nightDark};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: -2px;
    }
`;

const TabBadge = styled.span`
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 50px;
    background: ${colors.occasionalOrange}18;
    color: ${colors.occasionalOrange};
`;
